/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { slug } from '../../../common';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import AchievementUsersHero from './AchievementUsersHero';
import Avatar from 'flarum/common/components/Avatar';
import username from 'flarum/common/helpers/username';
import Link from 'flarum/common/components/Link';
import AchievementUsersListState from '../states/AchievementUsersListState';
import Button from 'flarum/common/components/Button';
import Placeholder from 'flarum/common/components/Placeholder';
const t = function () {
  return app.translator.trans(...arguments);
};
const prfx = `${slug}.forum.achievement`;
export default class AchievementUsersPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.levelId = m.route.param('levelId');
    this.state = new AchievementUsersListState(this.levelId);
    app.history.push('achievement');
    this.bodyClass = 'App--achievement';
    this.loading = false;
    this.error = null;
    this.loadMoreFailed = false;
    this.refresh();
  }
  view() {
    const {
      achievementLevel,
      loading,
      state,
      error
    } = this;
    let loadingUI = null;
    if (state.isInitialLoading() || state.isLoadingNext() || this.loading) {
      loadingUI = m(LoadingIndicator, null);
    } else if (state.hasNext()) {
      loadingUI = m(Button, {
        className: "Button Button--primary",
        onclick: async () => {
          this.loadMoreFailed = false;
          try {
            await state.loadNext();
          } catch (e) {
            // The list's request handler is silent, so this is the only surface; the button stays for a retry.
            this.loadMoreFailed = true;
          }
          m.redraw();
        }
      }, t(`${prfx}.load_more_button`));
      if (this.loadMoreFailed) {
        loadingUI = [m("div", {
          className: "AchievementUsersPage-loadMoreError"
        }, m(Placeholder, {
          text: t('core.lib.error.generic_message')
        })), loadingUI];
      }
    }
    if (error) {
      loadingUI = m(Placeholder, {
        text: t(error === 404 ? 'core.lib.error.not_found_message' : 'core.lib.error.generic_message')
      });
    } else if (state.isEmpty()) {
      loadingUI = m(Placeholder, {
        text: t(`${prfx}.be_the_first`)
      });
    }
    return m("div", {
      className: "AchievementUsersPage"
    }, m(AchievementUsersHero, {
      achievementLevel: achievementLevel,
      loading: loading
    }), m("div", {
      className: "container"
    }, m("div", {
      className: "AchievementUsersPage-users"
    }, state.getAllItems().map(user => m(Link, {
      href: app.route('user.achievements', {
        username: user.slug()
      })
    }, m("div", {
      className: "AchievementUsersPage-user"
    }, m(Avatar, {
      user: user
    }), username(user))))), m("div", {
      className: "AchievementUsersPage-loadMore"
    }, loadingUI)));
  }
  async refresh() {
    this.loading = true;
    this.error = null;
    m.redraw();
    try {
      this.achievementLevel = await app.store.find('achievement_levels', this.levelId, {}, {
        // The inline Placeholder is the one error surface; core's default alert would duplicate it.
        errorHandler: () => {}
      });
      await this.state.refresh();
    } catch (e) {
      this.error = e?.status ?? 500;
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
}
flarum.reg.add('glowingblue-gamilytics', 'forum/achievements/components/AchievementUsersPage', AchievementUsersPage);